const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY;

function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
  }
  return bytes;
}

let cryptoKey: CryptoKey | null = null;

async function getKey(): Promise<CryptoKey | null> {
  if (cryptoKey) return cryptoKey;
  if (!ENCRYPTION_KEY || ENCRYPTION_KEY.length !== 64) return null;

  cryptoKey = await crypto.subtle.importKey(
    "raw",
    hexToBytes(ENCRYPTION_KEY).buffer as ArrayBuffer,
    "AES-GCM",
    false,
    ["decrypt"],
  );
  return cryptoKey;
}

async function decryptPayload(
  encryptedB64: string,
  ivB64: string,
): Promise<string> {
  const key = await getKey();
  if (!key) throw new Error("Encryption key not available");

  const rawBytes = Uint8Array.from(atob(encryptedB64), (c) => c.charCodeAt(0));
  const iv = Uint8Array.from(atob(ivB64), (c) => c.charCodeAt(0));

  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: iv.buffer as ArrayBuffer },
    key,
    rawBytes.buffer as ArrayBuffer,
  );

  return new TextDecoder().decode(decrypted);
}

export async function decryptingFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const response = await fetch(input, init);

  try {
    const cloned = response.clone();
    const json = await cloned.json();

    if (json && typeof json.encrypted === "string" && typeof json.iv === "string") {
      const decryptedText = await decryptPayload(json.encrypted, json.iv);
      return new Response(decryptedText, {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      });
    }
  } catch {
    // Not JSON or decryption failed — return original response
  }

  return response;
}
